import React, { useCallback, useState } from 'react';
import { pick, identity, compose } from 'ramda';
import { useTranslations } from 'js/hooks/i18n';
import { I18n } from 'js/components/common/I18n';
import Select from 'js/components/common/Select';
import { useUserSelector } from 'js/store/hooks';
import InfoList from 'js/components/common/InfoList';
import { Loader } from 'js/components/common/Loader';
import { useDataFetch } from 'js/hooks/useDataFetch';
import { Button } from 'js/components/common/Button';
import { useMountEffect } from 'js/hooks/useMountEffect';
import { Toggle } from 'js/components/common/Fields/Toggle';
import { componentService } from 'js/services/component.service';
import { ErrorMessage } from 'js/components/common/ErrorMessage';
import { withComponent } from 'js/components/hocs/withComponent';
import { useComponentMethods } from 'js/hooks/component/useComponentMethods';

import { parse } from './helpers';
import { useGetErrorResponseMessage } from 'js/hooks/server';
import { useFetchPreferences, useUpdatePreferences } from './hooks';

import style from './TwoFactorAuthenticationPreferences.scss';

export const TwoFactorAuthenticationPreferencesBare = () => {
    const [formData, setFormData] = useState({});
    const [submitError, setSubmitError] = useState();
    const fetchPreferences = useFetchPreferences();
    const { showComponentError, showComponentMessage } = useComponentMethods();
    const getErrorResponseMessage = useGetErrorResponseMessage();
    const [{ isLoading, error, data }, refetch] = useDataFetch({
        fetch: fetchPreferences,
        onResponse: parse,
        onError: getErrorResponseMessage,
        shouldFetch: identity,
        initialIsLoading: false
    });
    const updatePreferences = useUpdatePreferences({
        initialValues: data?.initialValues, refetch, setSubmitError
    });

    const onToggleChange = useCallback((event) => {
        const { checked, name } = event.target;

        setFormData({ ...formData, [name]: checked });
    }, [formData, setFormData]);

    const onSelectChange = useCallback((event) => {
        const { value, name } = event.target;

        setFormData({ ...formData, [name]: value });
    }, [formData, setFormData]);

    const onSubmit = useCallback((event) => {
        event.preventDefault();

        updatePreferences(formData);
    }, [updatePreferences, formData]);

    const onCancel = useCallback(() => {
        const initialValues = data?.initialValues;

        if (initialValues) {
            setFormData({ ...initialValues });
        }
    }, [data, setFormData]);

    const { isLoggedIn } = useUserSelector(pick(['isLoggedIn']));
    const t = useTranslations('playtech.mobile.system.2FA.preferences.');

    useMountEffect((isMounted) => {
        if (!isMounted() || !isLoggedIn || isLoading) {
            return;
        }

        if (isMounted() && data) {
            setFormData({ ...data.formData });

            return;
        }

        if (isMounted() && !data && !error) {
            refetch(Date.now());
        }
    }, [isLoggedIn, isLoading, data, refetch, setFormData]);

    if (isLoading) {
        return <Loader />;
    }

    if (data && !data.enabled) {
        showComponentMessage(t('notEnabled'));
    }

    if (error) {
        showComponentError(error);
    }

    return (
        <form className={style.container} onSubmit={onSubmit}>
            {submitError && (
                <ErrorMessage
                    animate={true}
                    error={submitError}
                />
            )}
            <h3 className={style.title}>{t('title')}</h3>
            <InfoList className={style.list}>
                {Object.keys(formData)
                    .filter(key => key !== 'channel')
                    .map(key => <Toggle
                            key={key}
                            name={key}
                            checked={formData[key]}
                            className={style.toggle}
                            onChange={onToggleChange}
                            label={<I18n {...{ t, tKey: key }} />}
                        />
                    )}
                <label className={style.channel}>
                    <span>
                        <I18n {...{ t, tKey: 'channel' }} />
                    </span>
                    <Select
                        name="channel"
                        value={formData.channel}
                        onChange={onSelectChange}
                        aria-label={t(['channel', 'playtech.mobile.system.select.aria-label'])}
                        options={[{
                            value: 'sms',
                            label: t('channel.SMS')
                        }, {
                            value: 'email',
                            label: t('channel.email')
                        }]}
                    />
                </label>
            </InfoList>
            <div className={style.formActions}>
                <Button
                    type="button"
                    color='negative'
                    onClick={onCancel}
                    className={style.btn}
                >
                    {t('playtech.mobile.system.cancel.button.title')}
                </Button>
                <Button
                    type="submit"
                    className={style.btn}
                    disabled={isLoading}
                >
                    {t('button.submit')}
                </Button>
            </div>
        </form>
    );
};

export const TwoFactorAuthenticationPreferences = compose(
    withComponent({ isForLoggedIn: true })
)(TwoFactorAuthenticationPreferencesBare);

componentService.registerWebContentComponents({ TwoFactorAuthenticationPreferences });
